import { appstr } from "@coreapp/utils/appstr";
import { AssignmentStatus, kEmployeeAssignmentStatusID } from "@coreapp/constants";
import {
  Flex,
  Icon,
  type IconProps,
  Pill,
  PillWrapper,
  Text,
  TooltipWrapper
} from "@theorgchartdev/storybook";
import type { PillColors } from "@theorgchartdev/storybook/dist/components/Pill/Pill";
import type { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import type { ComponentProps } from "react";
import { Fragment } from "react";

export const AssignmentStatusIcons: Record<ValueOf<typeof AssignmentStatus>, IconProps> = {
  [AssignmentStatus.Assigned]: { name: "user", modifier: "warning" },
  [AssignmentStatus.Unassigned]: { name: "user-plus", modifier: "success" },
  [AssignmentStatus.Released]: { name: "user-slash", modifier: "secondary" },
  [AssignmentStatus.AssignedOutOfScope]: {
    name: "solid-user-circle-arrow-right",
    modifier: "purple"
  }
};

export const AssignmentStatusPillColors: Record<ValueOf<typeof AssignmentStatus>, PillColors> = {
  [AssignmentStatus.Assigned]: {
    color: "var(--txt-warning-primary)",
    backgroundColor: "var(--bg-warning-secondary)",
    borderColor: "var(--border-warning)"
  },
  [AssignmentStatus.Unassigned]: {
    color: "var(--txt-success-primary)",
    backgroundColor: "var(--bg-success-secondary)",
    borderColor: "var(--border-success)"
  },
  [AssignmentStatus.Released]: {
    color: "var(--txt-secondary-primary)",
    backgroundColor: "var(--bg-secondary-secondary)",
    borderColor: "var(--border-secondary)"
  },
  [AssignmentStatus.AssignedOutOfScope]: {
    color: "var(--utility-purple-900)",
    backgroundColor: "var(--utility-purple-50)",
    borderColor: "var(--utility-purple-600)"
  }
};

type Props = {
  record: Record<string, string>;
  dataObj?: Nullish<QObjDBObject>;
  withColon?: boolean;
  labelProps?: Partial<ComponentProps<typeof Text>>;
  showLabel?: boolean;
  tooltipProps?: any;
  renderBlank?: boolean;
};

export const isAssignmentStatus = (value?: unknown): value is ValueOf<typeof AssignmentStatus> => {
  return Object.values(AssignmentStatus).includes(value as ValueOf<typeof AssignmentStatus>);
};

export const AssignmentStatusPill = (props: Props) => {
  const { withColon = false, renderBlank = false } = props;
  const status =
    props.dataObj?.SmartGetFieldValue(kEmployeeAssignmentStatusID) ||
    props.record?.[kEmployeeAssignmentStatusID];
  const validatedStatus = isAssignmentStatus(status);

  if (!validatedStatus && !renderBlank) {
    return null;
  }

  const pillTooltipProps =
    status === AssignmentStatus.AssignedOutOfScope
      ? { message: appstr("Assigned Out Of Scope Info").toLocaleLowerCase(), ...props.tooltipProps }
      : props.tooltipProps;

  const hasTooltip = !!pillTooltipProps;
  const Wrapper = hasTooltip ? TooltipWrapper : Fragment;
  const wrapperProps = hasTooltip ? { tooltipProps: pillTooltipProps } : {};

  return (
    <Flex direction="row" alignItems="center" gap="8px">
      {(props.showLabel ?? true) && (
        <Text {...props?.labelProps}>
          {appstr("Assignment status")}
          {withColon ? ":" : ""}
        </Text>
      )}
      <PillWrapper>
        <Wrapper {...wrapperProps}>
          {validatedStatus ? (
            <Pill
              className="oc-relative"
              label={appstr(status).toLocaleLowerCase()}
              pillColors={AssignmentStatusPillColors[status]}
              leftContent={<Icon {...AssignmentStatusIcons[status]} size="small" />}
            />
          ) : (
            <Text>--</Text>
          )}
        </Wrapper>
      </PillWrapper>
    </Flex>
  );
};
